/**
 * OpenAI Ads Measurement Pixel user-matching fields.
 *
 * These go on `oaiq("init", { user })`, not on individual `measure` calls.
 * Hashed identifiers are SHA-256 hex of the normalized UTF-8 value.
 *
 * @see https://developers.openai.com/ads/measurement-pixel
 */

import { getCookie } from '@/core/utils/cookieUtils';
import { storageBuild } from '@/core/services/storage/config/storage.build';
import { getItem as getSessionItem } from '@/core/services/storage/utils/sessionStorageUtil';
import { getItem as getLocalItem } from '@/core/services/storage/utils/localStorageUtil';
import { OPENAI_OBREF_COOKIE, OPENAI_OPPREF_COOKIE } from '@/shared/features/analytics/constants';
import { getUserInfo, type UserInfo } from '@/shared/features/analytics/utils/commonAttributes';
import { getFacebookCommonAttributes } from '@/shared/features/analytics/utils/facebookCommonAttributes';

/** Measurement Pixel `user` object (`oaiq("init", { user })`).
 * Singular string fields — not CAPI lists like `emails_sha256`.
 * @see https://developers.openai.com/ads/measurement-pixel
 */
export interface OpenAiPixelUser {
  email_sha256?: string;
  phone_number_sha256?: string;
  external_id_sha256?: string;
  first_name_sha256?: string;
  last_name_sha256?: string;
  country?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  /** Alias the Pixel SDK also copies from `user` (`postal_code` is the documented name). */
  zip_code?: string;
}

/** Whitespace + ASCII punctuation; non-ASCII (accents) is preserved. */
const ASCII_WHITESPACE_AND_PUNCTUATION = /[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g;

export interface OpenAiUserOverrides {
  email?: unknown;
  phone?: unknown;
  userCode?: unknown;
  firstName?: unknown;
  lastName?: unknown;
}

export interface OpenAiNormalizedUserSource {
  email?: string;
  phone?: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  country: string;
  city?: string;
  region?: string;
  postalCode?: string;
}

function asNonEmptyString(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const trimmed = String(value).trim();
  return trimmed || undefined;
}

/** Trim whitespace, lowercase. */
export function normalizeOpenAiEmail(email: unknown): string | undefined {
  const trimmed = asNonEmptyString(email)?.toLowerCase();
  if (!trimmed || !trimmed.includes('@')) return undefined;
  return trimmed;
}

/**
 * Keep country calling code. Strip spaces, parentheses, periods, hyphens,
 * a leading `+`, and leading zeroes. Result must be 8–15 digits.
 * 10-digit Indian local numbers get `91` prepended so the calling code is kept.
 */
export function normalizeOpenAiPhone(phone: unknown): string | undefined {
  if (phone == null || phone === '') return undefined;
  let digits = String(phone)
    .trim()
    .replace(/[\s().-]/g, '')
    .replace(/^\+/, '')
    .replace(/^0+/, '');
  if (!digits) return undefined;
  if (/^\d{10}$/.test(digits)) {
    digits = `91${digits}`;
  }
  if (!/^\d{8,15}$/.test(digits)) return undefined;
  return digits;
}

/** Trim leading/trailing whitespace only. Case and all other characters preserved. */
export function normalizeOpenAiExternalId(id: unknown): string | undefined {
  return asNonEmptyString(id);
}

/** Lowercase, strip whitespace and ASCII punctuation. Accents are kept. */
export function normalizeOpenAiName(name: unknown): string | undefined {
  if (typeof name !== 'string') return undefined;
  const normalized = name.trim().toLowerCase().replace(ASCII_WHITESPACE_AND_PUNCTUATION, '');
  return normalized || undefined;
}

/** Two-letter ISO 3166-1 alpha-2. Defaults to IN. */
export function normalizeOpenAiCountry(country: unknown): string {
  if (typeof country === 'string') {
    const trimmed = country.trim();
    if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
    if (trimmed.toLowerCase() === 'india') return 'IN';
  }
  return 'IN';
}

/** City name, max 128 characters. Pixel docs: trim and lowercase. */
export function normalizeOpenAiCity(city: unknown): string | undefined {
  const trimmed = asNonEmptyString(city)?.toLowerCase();
  return trimmed ? trimmed.slice(0, 128) : undefined;
}

/** State / region, max 128 characters. */
export function normalizeOpenAiRegion(region: unknown): string | undefined {
  const trimmed = asNonEmptyString(region);
  return trimmed ? trimmed.slice(0, 128) : undefined;
}

/** Letters, numbers, spaces, or hyphens; max 32 characters. */
export function normalizeOpenAiPostalCode(code: unknown): string | undefined {
  if (code == null || code === '') return undefined;
  const sanitized = String(code)
    .trim()
    .replace(/[^A-Za-z0-9 -]/g, '')
    .slice(0, 32);
  return sanitized || undefined;
}

export async function sha256Hex(value: string): Promise<string | undefined> {
  if (typeof crypto === 'undefined' || typeof crypto.subtle?.digest !== 'function') {
    return undefined;
  }
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function firstDefined<T>(...values: Array<T | undefined>): T | undefined {
  return values.find((value) => value != null && value !== '') as T | undefined;
}

function readStoredUserInfo(): UserInfo | null {
  try {
    return getSessionItem<UserInfo>('userinfo') ?? getLocalItem<UserInfo>('userinfo');
  } catch {
    return null;
  }
}

function pickPostalCode(userInfo: UserInfo | null, metaZp: unknown): string | undefined {
  try {
    const { repo } = storageBuild();
    const storedPincode = repo.localGetData<string>('pincode');
    return firstDefined(
      normalizeOpenAiPostalCode(metaZp),
      normalizeOpenAiPostalCode(userInfo?.pincode),
      normalizeOpenAiPostalCode(storedPincode),
    );
  } catch {
    return firstDefined(
      normalizeOpenAiPostalCode(metaZp),
      normalizeOpenAiPostalCode(userInfo?.pincode),
    );
  }
}

function pickCity(userInfo: UserInfo | null, meta: Record<string, unknown>): string | undefined {
  return firstDefined(
    normalizeOpenAiCity(meta.city),
    normalizeOpenAiCity(meta.ct),
    normalizeOpenAiCity(userInfo?.cityName),
    normalizeOpenAiCity(userInfo?.deliveryCity),
    normalizeOpenAiCity(userInfo?.city),
    normalizeOpenAiCity(userInfo?.locality),
  );
}

/**
 * Collects Measurement Pixel user fields. Email / city / ZIP are read from
 * Meta attrs, `/user-context`, and stored profile/pincode — guessed pincode
 * is still sent so ZIP is not dropped the way Meta's `zp` is.
 */
export function collectOpenAiUserSource(
  overrides: OpenAiUserOverrides = {},
  userInfo: UserInfo | null = getUserInfo(),
): OpenAiNormalizedUserSource {
  const meta = typeof window === 'undefined' ? {} : getFacebookCommonAttributes();
  const stored = readStoredUserInfo();

  return {
    email: firstDefined(
      normalizeOpenAiEmail(overrides.email),
      normalizeOpenAiEmail(meta.em),
      normalizeOpenAiEmail(userInfo?.email),
      normalizeOpenAiEmail(stored?.email),
    ),
    phone: firstDefined(
      normalizeOpenAiPhone(overrides.phone),
      normalizeOpenAiPhone(meta.ph),
      normalizeOpenAiPhone(userInfo?.phoneNumber),
      normalizeOpenAiPhone(stored?.phoneNumber),
    ),
    externalId:
      normalizeOpenAiExternalId(overrides.userCode) ??
      normalizeOpenAiExternalId(meta.external_id),
    firstName: normalizeOpenAiName(overrides.firstName) ?? normalizeOpenAiName(meta.fn),
    lastName: normalizeOpenAiName(overrides.lastName) ?? normalizeOpenAiName(meta.ln),
    country: normalizeOpenAiCountry(meta.country ?? userInfo?.country),
    city: pickCity(userInfo ?? stored, meta),
    region: firstDefined(
      normalizeOpenAiRegion(meta.st),
      normalizeOpenAiRegion(userInfo?.state),
      normalizeOpenAiRegion(stored?.state),
    ),
    postalCode: pickPostalCode(userInfo ?? stored, meta.zp),
  };
}

export function fingerprintOpenAiUserSource(source: OpenAiNormalizedUserSource): string {
  return JSON.stringify(source);
}

export async function toOpenAiPixelUser(
  source: OpenAiNormalizedUserSource,
): Promise<OpenAiPixelUser> {
  const [
    email_sha256,
    phone_number_sha256,
    external_id_sha256,
    first_name_sha256,
    last_name_sha256,
  ] = await Promise.all([
    source.email ? sha256Hex(source.email) : Promise.resolve(undefined),
    source.phone ? sha256Hex(source.phone) : Promise.resolve(undefined),
    source.externalId ? sha256Hex(source.externalId) : Promise.resolve(undefined),
    source.firstName ? sha256Hex(source.firstName) : Promise.resolve(undefined),
    source.lastName ? sha256Hex(source.lastName) : Promise.resolve(undefined),
  ]);

  return compactRecord({
    email_sha256,
    phone_number_sha256,
    external_id_sha256,
    first_name_sha256,
    last_name_sha256,
    country: source.country,
    city: source.city,
    region: source.region,
    postal_code: source.postalCode,
    zip_code: source.postalCode,
  }) as OpenAiPixelUser;
}

/**
 * Cache of the most recently computed hashed Pixel user, kept in sync by
 * `OpenAiAdapter.syncUserAndInit()`. SHA-256 hashing is unavoidably async
 * (`crypto.subtle.digest`), so server-event payloads (built synchronously
 * inside `trackEvent`) read this cache rather than re-hashing per event —
 * the same one-tick-behind tradeoff the Pixel's own `init` call already has.
 */
let cachedHashedOpenAiUser: OpenAiPixelUser | null = null;

export function setCachedOpenAiHashedUser(user: OpenAiPixelUser | null): void {
  cachedHashedOpenAiUser = user;
}

export function getCachedOpenAiHashedUser(): OpenAiPixelUser | null {
  return cachedHashedOpenAiUser;
}

/** Conversions API `user` object shape: list fields, plus obref/ip/UA. */
export interface OpenAiConversionsApiUser {
  emails_sha256?: string[];
  phone_numbers_sha256?: string[];
  external_ids_sha256?: string[];
  first_names_sha256?: string[];
  last_names_sha256?: string[];
  countries?: string[];
  cities?: string[];
  regions?: string[];
  postal_codes?: string[];
  obref?: string;
  ip_address?: string;
  user_agent?: string;
}

/**
 * Read the OpenAI Pixel's `__obref` first-party cookie, to forward unhashed
 * as `user.obref` for hybrid Pixel + Conversions API deduplication.
 * @see https://developers.openai.com/ads/conversions-api
 */
export function getOpenAiObref(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return getCookie(OPENAI_OBREF_COOKIE)?.trim() || undefined;
}

function getOpenAiClientIpAddress(): string | undefined {
  const ip = getUserInfo()?.client_ip;
  return typeof ip === 'string' && ip.trim() ? ip.trim() : undefined;
}

/**
 * Reshapes the (singular-field) Pixel user + obref/ip/UA into the Conversions
 * API's `events[].user` shape (plural `_sha256` list fields, raw geo lists).
 * @see https://developers.openai.com/ads/conversions-api#send-user-data
 */
export function toOpenAiConversionsApiUser(
  hashedUser: OpenAiPixelUser | null,
): OpenAiConversionsApiUser | undefined {
  const user: OpenAiConversionsApiUser = compactRecord({
    emails_sha256: hashedUser?.email_sha256 ? [hashedUser.email_sha256] : undefined,
    phone_numbers_sha256: hashedUser?.phone_number_sha256 ? [hashedUser.phone_number_sha256] : undefined,
    external_ids_sha256: hashedUser?.external_id_sha256 ? [hashedUser.external_id_sha256] : undefined,
    first_names_sha256: hashedUser?.first_name_sha256 ? [hashedUser.first_name_sha256] : undefined,
    last_names_sha256: hashedUser?.last_name_sha256 ? [hashedUser.last_name_sha256] : undefined,
    countries: hashedUser?.country ? [hashedUser.country] : undefined,
    cities: hashedUser?.city ? [hashedUser.city] : undefined,
    regions: hashedUser?.region ? [hashedUser.region] : undefined,
    postal_codes: hashedUser?.postal_code ? [hashedUser.postal_code] : undefined,
    obref: getOpenAiObref(),
    ip_address: getOpenAiClientIpAddress(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }) as OpenAiConversionsApiUser;

  return Object.keys(user).length > 0 ? user : undefined;
}

function compactRecord<T extends Record<string, unknown>>(record: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(record).filter(([, value]) => value != null && value !== ''),
  ) as Partial<T>;
}

export function overridesFromEventProperties(
  properties: Record<string, unknown>,
): OpenAiUserOverrides {
  return {
    email: properties.email ?? properties.em,
    phone: properties.phone ?? properties.ph,
    userCode: properties.userCode ?? properties.external_id,
    firstName: properties.firstName ?? properties.first_name,
    lastName: properties.lastName ?? properties.last_name,
  };
}

/**
 * OpenAI click reference (`oppref`). Event-level, not hashed, not inside `user`.
 * Read the landing URL first, then the Pixel's `__oppref` cookie. Do not write
 * that cookie — the SDK stores it the same way Meta stores `_fbc`.
 */
export function getOpenAiOppref(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const fromQuery = new URLSearchParams(window.location.search).get('oppref')?.trim();
    if (fromQuery) return fromQuery;
  } catch {
    // ignore invalid URL search
  }

  return getCookie(OPENAI_OPPREF_COOKIE)?.trim() || undefined;
}
